import { ActualItemOrigin, ItemClass, ItemClasses, PricingType, PricingTypes } from '../common/constants'
import { hasValue } from '../helpers/nullable'
import { uniqueId } from '../helpers/uniqueId'
import { YearlyBudget } from './budget'
import { CatalogItem } from './catalogItem'

export type WorkareaItemBase = {
  id: number
  guid: string
  tenantId: number
  workareaId: number
  catalogItemId: number
  catalogItemGuid: string
  name: string
  catalogName: string
  units: string
  class: ItemClass
  quantity: number
  isBillable: boolean
  customCost?: number
  customUnitPrice?: number
  customProfitMargin?: number
  cost: number
  overhead: number
  tax: number
  profit: number
  originalCatalogCustomPrice?: number
  catalogCustomPrice?: number
  catalogCustomProfitMargin?: number
  catalogCost: number
  chargeTaxOnInvoice: boolean
  applyTaxToBreakeven: boolean
  originalProfitMargin: number
  taxPercentage: number
  overheadPercentage: number

  sortIndex: number

  profitMargin: number
  breakeven: number
  basePrice: number
  unitPrice: number
  totalPrice: number
  totalBreakeven: number
}

export type EstimatedItem = WorkareaItemBase & {
  templateItemId?: number
}

export type ActualItem = WorkareaItemBase & {
  isBillableUnknown: boolean
  isAdditional: boolean
  visitId?: string
  invoiceId?: number
  origin?: ActualItemOrigin
  originOwner?: string
  isFromTimesheets: boolean
  customTotalPrice?: number
  receiptId?: number
  webEntryConsumptionDate?: string
}

export const isActualItem = (item: WorkareaItemBase): item is ActualItem => {
  return (item as ActualItem).isAdditional !== undefined
}

export const isEstimatedItem = (item: WorkareaItemBase): item is EstimatedItem => !isActualItem(item)

type CreateItemFromCatalogInput = {
  workareaId: number
  catalogItem: CatalogItem
  budgets: YearlyBudget[]
  pricingType: PricingType
  quantity: number
  sortIndex: number
  name?: string
}

export type CreateEstimatedItemFromCatalogInput = CreateItemFromCatalogInput & {
  templateItemId?: number
}

export type CreateActualItemFromCatalogInput = CreateItemFromCatalogInput & {
  visitId?: string
  origin?: ActualItemOrigin
  receiptId?: number
}

export const defaultWorkareaItemBase = (): WorkareaItemBase => {
  return {
    id: 0,
    guid: '',
    tenantId: 0,
    workareaId: 0,
    catalogItemId: 0,
    catalogItemGuid: '',
    name: '',
    catalogName: '',
    units: '',
    class: ItemClasses.Materials,
    quantity: 0,
    isBillable: false,
    chargeTaxOnInvoice: false,
    sortIndex: 0,
    catalogCost: 0,
    cost: 0,
    overhead: 0,
    tax: 0,
    profit: 0,
    originalProfitMargin: 0,
    profitMargin: 0,
    taxPercentage: 0,
    overheadPercentage: 0,
    breakeven: 0,
    basePrice: 0,
    unitPrice: 0,
    totalPrice: 0,
    totalBreakeven: 0,
    applyTaxToBreakeven: false
  }
}

export const defaultEstimatedItem = (): EstimatedItem => {
  return { ...defaultWorkareaItemBase() }
}

export const defaultActualItem = (): ActualItem => {
  return {
    ...defaultWorkareaItemBase(),
    isBillableUnknown: false,
    isAdditional: false,
    isFromTimesheets: false,
  }
}

/** Ignore retired wire fields during the coordinated item-to-work-area cutover. */
export const withoutItemRecovery = <T extends WorkareaItemBase>(item: T): T => {
  const { recovery, recoveryPercentage, totalRecovery, bankUnitPrice, hasCustomPrice, ...workItem } = item as T & {
    recovery?: number; recoveryPercentage?: number; totalRecovery?: number; bankUnitPrice?: number; hasCustomPrice?: boolean
  }
  return workItem as T
}

export const recalculateWorkareaItemPricing = <T extends WorkareaItemBase>(item: T): T => {
  const actual = isActualItem(item) ? item as ActualItem : undefined
  const tax = item.applyTaxToBreakeven ? Math.round(item.cost * item.taxPercentage) : 0
  const overhead = Math.round(item.cost * item.overheadPercentage)
  const breakeven = item.cost + tax + overhead
  const customPrice = item.customUnitPrice ?? item.originalCatalogCustomPrice
  const basePrice = customPrice ?? calculateUnitPrice(breakeven, item.profitMargin)
  // Payment processing belongs to the work area, never to an item price.
  const unitPrice = basePrice
  const profit = unitPrice - breakeven
  return {
    ...withoutItemRecovery(item),
    tax,
    overhead,
    breakeven,
    basePrice,
    unitPrice,
    profit,
    totalBreakeven: Math.round(breakeven * item.quantity),
    totalPrice: !item.isBillable ? 0 : Math.round(actual?.customTotalPrice ?? unitPrice * item.quantity),
  }
}

export const calculateUnitPrice = (breakeven: number, profitMargin: number) => {
  return Math.round(breakeven / (1 - profitMargin))
}

export const updateName = (item: WorkareaItemBase, value: string): WorkareaItemBase => {
  return { ...item, name: value ? value : item.catalogName }
}

export const updateIsAdditional = (item: ActualItem, pricingType: PricingType, isVisitReviewed: boolean, value: boolean): ActualItem => {
  if (isAdditionalLocked(item, isVisitReviewed)) {
    return { ...item }
  }

  const updated: ActualItem = { ...item, isAdditional: value }

  if (!value && isBillableLocked(updated, pricingType, isVisitReviewed)) {
    return recalculateWorkareaItemPricing({ ...item, isAdditional: false, isBillable: defaultIsBillable(pricingType, item.class) })
  }

  return updated
}

export const updateIsBillableUnknown = (item: ActualItem, pricingType: PricingType, isVisitReviewed: boolean, value: boolean): ActualItem => {
  if (isBillableLocked(item, pricingType, isVisitReviewed)) {
    return { ...item }
  }

  return recalculateWorkareaItemPricing({ ...item, isBillableUnknown: value, isBillable: false })
}

export const updateIsBillable = (item: WorkareaItemBase, pricingType: PricingType, isVisitReviewed: boolean, value: boolean): WorkareaItemBase => {
  if (isBillableLocked(item, pricingType, isVisitReviewed)) {
    return { ...item }
  }

  if (isActualItem(item)) {
    return recalculateWorkareaItemPricing({ ...item, isBillable: value, isBillableUnknown: false })
  }

  return recalculateWorkareaItemPricing({ ...item, isBillable: value })
}

export const updateIsBillableForPricingTypeChange = (item: WorkareaItemBase, value: boolean): WorkareaItemBase => {
  if (isActualItem(item)) {
    return recalculateWorkareaItemPricing({ ...item, isBillable: value, isBillableUnknown: false })
  }

  return recalculateWorkareaItemPricing({ ...item, isBillable: value })
}

export const updateChargeTaxOnInvoice = (item: WorkareaItemBase, isVisitReviewed: boolean, value: boolean): WorkareaItemBase => {
  const actual = item as ActualItem
  if (isVisitReviewed || hasValue(actual.invoiceId)) {
    return { ...item }
  }
  return { ...item, chargeTaxOnInvoice: value }
}

export const updateQuantity = (item: WorkareaItemBase, value: number): WorkareaItemBase => {
  return recalculateWorkareaItemPricing({ ...item, quantity: Math.max(value, 0) })
}

export const updateCustomCost = (item: WorkareaItemBase, value: number | null): WorkareaItemBase => {
  const customCost = undefinedIfMatch(value, item.catalogCost)
  return recalculateWorkareaItemPricing({ ...item, cost: customCost ?? item.catalogCost, customCost })
}

export const updateCustomProfitMargin = (item: WorkareaItemBase, value: number | null): WorkareaItemBase => {
  const defaultValue = item.catalogCustomProfitMargin ?? item.originalProfitMargin
  const customProfitMargin = undefinedIfMatch(value, defaultValue)
  const profitMargin = customProfitMargin ?? defaultValue
  return recalculateWorkareaItemPricing({ ...item, customProfitMargin, profitMargin })
}

export const updateCustomUnitPrice = (item: WorkareaItemBase, value: number | null): WorkareaItemBase => {
  const defaultUnitPrice = recalculateWorkareaItemPricing({ ...item, customUnitPrice: undefined }).unitPrice
  const customUnitPrice = undefinedIfMatch(value, defaultUnitPrice)
  return recalculateWorkareaItemPricing({ ...item, customUnitPrice })
}

export const updateCustomTotalPrice = (item: ActualItem, value: number | null): ActualItem => {
  const defaultTotalPrice = recalculateWorkareaItemPricing({ ...item, customTotalPrice: undefined }).totalPrice
  const customTotalPrice = undefinedIfMatch(value, defaultTotalPrice)
  return recalculateWorkareaItemPricing({ ...item, customTotalPrice })
}

const undefinedIfMatch = (value: number | undefined | null, match: number): number | undefined => {
  if (value === match) {
    return undefined
  }
  return value ?? undefined
}

export const defaultIsBillable = (pricingType: PricingType, itemClass: ItemClass): boolean => {
  if (pricingType === PricingTypes.Unbillable) {
    return false
  }

  if (pricingType === PricingTypes.FlatRate) {
    return itemClass !== ItemClasses.Labor
  }

  return true
}

export const isBillableLocked = (item: WorkareaItemBase, pricingType: PricingType, isVisitReviewed: boolean): boolean => {
  const actual = item as ActualItem

  if (isVisitReviewed || hasValue(actual.invoiceId)) {
    return true
  }

  if (actual.isAdditional) {
    return false
  }

  switch (pricingType) {
    case PricingTypes.TimeAndMaterials: return false
    case PricingTypes.FlatRate: return item.class === ItemClasses.Labor
    default: return true
  }
}

export const isAdditionalLocked = (actual: ActualItem, isVisitReviewed: boolean): boolean => {
  return (isVisitReviewed || hasValue(actual.invoiceId))
}

export const generateNewItemId = (): string => {
  return uniqueId()
}

const createNewItem = (input: CreateItemFromCatalogInput): WorkareaItemBase => {
  const budget = input.budgets.find(x => x.class === input.catalogItem.class)
  if (!budget) {
    throw new Error('Missing budget')
  }

  return {
    id: 0,
    guid: generateNewItemId(),
    tenantId: input.catalogItem.tenantId,
    workareaId: input.workareaId,
    catalogItemId: input.catalogItem.id,
    catalogItemGuid: input.catalogItem.guid,
    name: input.name ?? input.catalogItem.name,
    catalogName: input.catalogItem.name,
    units: input.catalogItem.units ?? '',
    class: input.catalogItem.class,
    quantity: input.quantity,
    isBillable: defaultIsBillable(input.pricingType, input.catalogItem.class),
    cost: input.catalogItem.adjustedCost,
    catalogCost: input.catalogItem.adjustedCost,
    overhead: 0,
    tax: 0,
    profit: 0,
    originalCatalogCustomPrice: input.catalogItem.customPrice,
    catalogCustomPrice: input.catalogItem.customPrice,
    catalogCustomProfitMargin: input.catalogItem.customProfitMargin,
    chargeTaxOnInvoice: false,
    originalProfitMargin: budget.profit / 100,
    taxPercentage: budget.taxRate / 100,
    overheadPercentage: budget.overhead / 100,
    sortIndex: input.sortIndex,
    basePrice: 0,
    profitMargin: input.catalogItem.customProfitMargin ?? budget.profit / 100,
    breakeven: 0,
    unitPrice: 0,
    totalPrice: 0,
    totalBreakeven: 0,
    applyTaxToBreakeven: input.catalogItem.taxable
  }
}

export const createEstimatedItemFromCatalogItem = (input: CreateEstimatedItemFromCatalogInput): EstimatedItem => {
  const newItem: EstimatedItem = {
    ...createNewItem(input),
    templateItemId: input.templateItemId
  }

  return recalculateWorkareaItemPricing(newItem)
}

export const createActualItemFromCatalogItem = (input: CreateActualItemFromCatalogInput): ActualItem => {
  if (!input.visitId && !input.origin) {
    throw new Error('Missing visitId/origin')
  }

  const newItem: ActualItem = {
    ...createNewItem(input),
    isBillableUnknown: false,
    isAdditional: false,
    visitId: input.visitId,
    origin: input.origin,
    isFromTimesheets: false,
    receiptId: input.receiptId,
  }

  return recalculateWorkareaItemPricing(newItem) as ActualItem
}

export const checkPricingSync = (workareaItem: WorkareaItemBase, catalogItem: CatalogItem, budgets: YearlyBudget[]): boolean => {
  if ([workareaItem.customCost, workareaItem.customProfitMargin, workareaItem.customUnitPrice].some(hasValue)) {
    return true
  }

  const standardPrice = syncPricing(workareaItem, catalogItem, budgets).unitPrice
  const currentPrice = recalculateWorkareaItemPricing(workareaItem).unitPrice

  return standardPrice === currentPrice
}

type SyncPricingCatalogItem = Pick<CatalogItem, 'adjustedCost' | 'customPrice' | 'customProfitMargin' | 'taxable'>
export const syncPricing = <T extends WorkareaItemBase>(workareaItem: T, catalogItem: SyncPricingCatalogItem, budgets: YearlyBudget[]): T => {
  const budget = budgets.find(x => x.class === workareaItem.class)!
  const taxPercentage = budget.taxRate / 100
  const overheadPercentage = budget.overhead / 100
  const budgetProfitMargin = budget.profit / 100
  const profitMargin = workareaItem.customProfitMargin ?? catalogItem.customProfitMargin ?? budgetProfitMargin
  return recalculateWorkareaItemPricing({
    ...workareaItem,
    cost: catalogItem.adjustedCost,
    catalogCost: catalogItem.adjustedCost,
    originalCatalogCustomPrice: catalogItem.customPrice,
    catalogCustomPrice: catalogItem.customPrice,
    catalogCustomProfitMargin: catalogItem.customProfitMargin,
    applyTaxToBreakeven: catalogItem.taxable,
    taxPercentage,
    overheadPercentage,
    profitMargin,
    originalProfitMargin: budgetProfitMargin,
  })
}

export const copyToWorkarea = <T extends WorkareaItemBase>(item: T, newWorkareaId: number): T => {
  return { ...item, id: 0, workareaId: newWorkareaId, guid: uniqueId() }
}
